import React from 'react';
import { Link } from 'react-router-dom';
import { 
  UserPlus, 
  CheckSquare, 
  Users, 
  Monitor, 
  BarChart3,
  ArrowRight,
  Building,
  Shield,
  Zap,
  Clock
} from 'lucide-react';
import Button from '../components/common/Button';
import Card from '../components/common/Card';

const Home: React.FC = () => {
  const features = [
    {
      icon: UserPlus,
      title: 'Đăng ký khách vãng lai',
      description: 'Khách tự đăng ký thông tin qua kiosk/tablet với giao diện thân thiện',
      path: '/guest-registration',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      icon: CheckSquare,
      title: 'Duyệt đăng ký',
      description: 'BQL nhận thông báo và duyệt/từ chối đăng ký khách vãng lai',
      path: '/approval',
      color: 'bg-green-100 text-green-600'
    },
    {
      icon: Users,
      title: 'Lễ tân cấp thẻ',
      description: 'Tra cứu khách đã duyệt và cấp thẻ truy cập tòa nhà',
      path: '/reception',
      color: 'bg-purple-100 text-purple-600'
    },
    {
      icon: Monitor,
      title: 'Màn hình turnstile',
      description: 'Hiển thị thông tin khách và hướng dẫn di chuyển khi quét thẻ',
      path: '/turnstile',
      color: 'bg-orange-100 text-orange-600'
    },
    {
      icon: BarChart3,
      title: 'Thống kê & báo cáo',
      description: 'Dashboard theo dõi lưu lượng ra vào và phân tích dữ liệu',
      path: '/statistics',
      color: 'bg-red-100 text-red-600'
    }
  ];

  const benefits = [
    {
      icon: Shield,
      title: 'Bảo mật cao',
      description: 'Kiểm soát chặt chẽ người ra vào tòa nhà'
    },
    {
      icon: Zap,
      title: 'Tự động hóa',
      description: 'Giảm thiểu thao tác thủ công, tăng hiệu quả'
    },
    {
      icon: Clock,
      title: 'Tiết kiệm thời gian',
      description: 'Quy trình nhanh chóng, không cần chờ đợi'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="flex items-center justify-center mb-8">
              <div className="w-20 h-20 bg-primary-500 rounded-2xl flex items-center justify-center shadow-lg">
                <Building className="w-10 h-10 text-white" />
              </div>
            </div>
            <h1 className="text-5xl font-bold text-secondary-900 mb-6">
              Hệ Thống Quản Lý Ra Vào Tòa Nhà
            </h1>
            <p className="text-xl text-secondary-600 mb-8 max-w-3xl mx-auto">
              Giải pháp toàn diện cho việc quản lý khách vãng lai với 4 lanes turnstile/tripod, 
              từ đăng ký đến kiểm soát ra vào tự động
            </p>
            <div className="flex items-center justify-center space-x-4">
              <Link to="/guest-registration">
                <Button size="xl">
                  Bắt đầu demo
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/statistics">
                <Button variant="secondary" size="xl">
                  Xem thống kê
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-secondary-900 mb-4">
            Các tính năng chính
          </h2>
          <p className="text-lg text-secondary-600">
            Hệ thống bao gồm 5 màn hình chính phục vụ toàn bộ quy trình
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Card key={index} className="hover:shadow-xl transition-all duration-300 group cursor-pointer">
                <Link to={feature.path} className="block">
                  <div className="text-center">
                    <div className={`w-16 h-16 ${feature.color} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-secondary-600 mb-4">
                      {feature.description}
                    </p>
                    <div className="flex items-center justify-center text-primary-600 font-medium group-hover:text-primary-700">
                      Xem demo
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-secondary-900 mb-4">
              Lợi ích của hệ thống
            </h2>
            <p className="text-lg text-secondary-600">
              Mang lại giá trị thiết thực cho việc quản lý tòa nhà
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-primary-100 text-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-secondary-600">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Demo Flow Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-secondary-900 mb-4">
            Quy trình demo
          </h2>
          <p className="text-lg text-secondary-600">
            Trải nghiệm toàn bộ flow từ đăng ký đến ra vào tòa nhà
          </p>
        </div>

        <Card className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {[
              { step: 1, title: 'Đăng ký', desc: 'Khách tự đăng ký', path: '/guest-registration' },
              { step: 2, title: 'Duyệt', desc: 'BQL phê duyệt', path: '/approval' },
              { step: 3, title: 'Cấp thẻ', desc: 'Lễ tân cấp thẻ', path: '/reception' },
              { step: 4, title: 'Quét thẻ', desc: 'Vào turnstile', path: '/turnstile' },
              { step: 5, title: 'Thống kê', desc: 'Xem báo cáo', path: '/statistics' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <Link to={item.path} className="block group">
                  <div className="w-12 h-12 bg-primary-500 text-white rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-primary-600 transition-colors">
                    {item.step}
                  </div>
                  <h4 className="font-semibold text-secondary-900 mb-1 group-hover:text-primary-600 transition-colors">
                    {item.title}
                  </h4>
                  <p className="text-sm text-secondary-600">
                    {item.desc}
                  </p>
                </Link>
                {index < 4 && (
                  <div className="hidden md:block absolute top-6 left-full w-full">
                    <ArrowRight className="w-4 h-4 text-secondary-400 mx-auto" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Home;
